import React, { InputHTMLAttributes } from 'react';
import { Switch } from '@headlessui/react';
import { useField } from 'formik';
import classNames from 'classnames';
import { useErrorText } from '../hooks/useErrorText';
import { SelectValue } from '../selectTW/interfaces';
import { mergeTests } from '../../ui-toolkit/test-utilities/mergeTests';

interface ButtonTWProps extends InputHTMLAttributes<HTMLButtonElement> {
  name: string;
  className?: string;
  text?: string;
  onValues?: string[];
  offValues?: string[];
}

export const StringToggleTW: React.FC<ButtonTWProps> = ({
  name,
  text,
  className,
  onValues = ['true', 'TRUE'],
  offValues = ['false', 'FALSE'],
  ...rest
}) => {
  const [fieldInputProps, fieldMetaProps, helper] = useField<string>(name);
  const errorText = useErrorText<SelectValue>(fieldMetaProps);

  const onChange = (value: boolean) => {
    helper.setTouched(true);
    helper.setValue(value ? onValues[0] : offValues[0]);
  };

  const checked: boolean = onValues.includes(fieldInputProps.value);

  return (
    <Switch.Group as="div" className={classNames('flex items-center', className)}>
      <Switch
        checked={checked}
        onChange={onChange}
        className={classNames(
          {
            'bg-foundation-primary': checked,
            'bg-gray-200': !checked,
          },
          'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-foundation-primary focus:ring-offset-2'
        )}
        data-test-id={mergeTests(rest['data-test-id'], 'toggle-tailwind')}
      >
        <span
          aria-hidden="true"
          className={classNames(
            {
              'translate-x-5': checked,
              'translate-x-0': !checked,
            },
            'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out'
          )}
        />
      </Switch>
      <Switch.Label as="span" className="ml-3 text-sm">
        <span
          className={classNames('font-medium text-gray-900', {
            'text-text-error': !!errorText,
            'text-text-default-content': !errorText,
          })}
        >
          {text}
        </span>
      </Switch.Label>
    </Switch.Group>
  );
};
